import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp,
  Activity,
  Trash2,
  LineChart as LineChartIcon,
  ExternalLink,
  AlertCircle,
  Loader2,
  PlusCircle,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardTitle, CardDescription, Input, Button, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { RankChannel, RankCheckResult, RankTargetItem } from '@keywordgod/shared';

const CHANNEL_LABEL: Record<RankChannel, string> = {
  view: '통합 (VIEW)',
  blog: '블로그',
  web: '웹',
};

export function Rank() {
  const [selected, setSelected] = useState<{ keyword: string; url: string } | null>(null);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <TrendingUp className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">순위 추적</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          네이버 검색에서 내 URL 의 1~100 위 순위를 추적합니다. 추적 등록 후엔 매 2분 cron 이
          24시간 이상 안 된 항목을 자동 갱신합니다.
        </p>
      </header>

      <QuickCheckCard />
      <MyTargetsCard onSelect={(t) => setSelected({ keyword: t.keyword, url: t.url })} selected={selected} />
      {selected && (
        <HistoryCard
          keyword={selected.keyword}
          url={selected.url}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ─── 즉시 체크 ──────────────────────────────────────────────

function QuickCheckCard() {
  const [keyword, setKeyword] = useState('');
  const [url, setUrl] = useState('');
  const [channel, setChannel] = useState<RankChannel>('view');
  const [result, setResult] = useState<RankCheckResult | null>(null);
  const qc = useQueryClient();

  const check = useMutation({
    mutationFn: () => api.rank.check(keyword.trim(), url.trim(), channel),
    onSuccess: (r) => setResult(r),
  });

  const addTarget = useMutation({
    mutationFn: () => api.rank.addTarget(keyword.trim(), url.trim()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rank', 'targets'] }),
  });

  const canRun = !!keyword.trim() && !!url.trim();

  return (
    <Card>
      <CardTitle className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-gold" /> 즉시 순위 체크
      </CardTitle>
      <CardDescription>
        키워드 + 페이지 URL 입력 → 1~100위 안에서 위치 즉시 확인 (네이버 검색 페이지 1~10 스캔)
      </CardDescription>

      <form
        className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto_auto] gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (canRun) check.mutate();
        }}
      >
        <Input
          placeholder="키워드 (예: 다이어트 보조제)"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Input
          placeholder="URL (예: https://blog.naver.com/.../12345)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as RankChannel)}
          className="rounded-lg border border-gray-800 bg-gray-950 px-3 text-sm text-white focus:outline-none focus:border-gold"
          aria-label="검색 채널"
        >
          {(Object.entries(CHANNEL_LABEL) as [RankChannel, string][]).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <Button type="submit" disabled={!canRun || check.isPending}>
          {check.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> 체크 중…
            </>
          ) : (
            '순위 체크'
          )}
        </Button>
      </form>

      {check.isError && (
        <p className="mt-3 text-sm text-red-400">
          <AlertCircle className="inline h-4 w-4 mr-1" />
          {(check.error as Error).message}
        </p>
      )}

      {result && (
        <div className="mt-5 rounded-xl border border-gold/30 bg-gold/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-gray-400">{result.keyword}</div>
              <div className="truncate text-sm text-gray-200">{result.url}</div>
              <div className="mt-1 text-[11px] text-gray-500">
                {CHANNEL_LABEL[result.channel as RankChannel] ?? result.channel} ·{' '}
                {new Date(result.checkedAt * 1000).toLocaleString('ko-KR')}
              </div>
            </div>
            <PositionBadge position={result.position} page={result.page} />
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => addTarget.mutate()}
              disabled={addTarget.isPending || addTarget.isSuccess}
            >
              {addTarget.isSuccess ? (
                '추가됨 ✓'
              ) : addTarget.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> 추가 중
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4" /> 자동 추적에 추가
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function PositionBadge({ position, page }: { position: number | null; page: number | null }) {
  if (position == null) {
    return (
      <div className="rounded-lg bg-red-500/15 border border-red-500/40 px-4 py-2 text-center">
        <div className="text-[10px] uppercase tracking-widest text-red-300">결과 없음</div>
        <div className="text-sm font-bold text-red-200 mt-0.5">100위 밖</div>
      </div>
    );
  }
  // 1~10 emerald, 11~30 gold, 31~100 gray
  const color =
    position <= 10
      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
      : position <= 30
      ? 'bg-gold/15 border-gold/40 text-gold'
      : 'bg-gray-800 border-gray-700 text-gray-200';
  return (
    <div className={cn('rounded-lg border px-4 py-2 text-center', color)}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">
        {page != null ? `${page}페이지` : '순위'}
      </div>
      <div className="tabular text-2xl font-extrabold mt-0.5">{position}위</div>
    </div>
  );
}

// ─── 내 추적 키워드 목록 ────────────────────────────────────

function MyTargetsCard({
  onSelect,
  selected,
}: {
  onSelect: (t: { keyword: string; url: string }) => void;
  selected: { keyword: string; url: string } | null;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['rank', 'targets'],
    queryFn: () => api.rank.targets(),
    staleTime: 30_000,
  });

  const [keyword, setKeyword] = useState('');
  const [url, setUrl] = useState('');

  const add = useMutation({
    mutationFn: () => api.rank.addTarget(keyword.trim(), url.trim()),
    onSuccess: () => {
      setKeyword('');
      setUrl('');
      qc.invalidateQueries({ queryKey: ['rank', 'targets'] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.rank.deleteTarget(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rank', 'targets'] }),
  });

  const canAdd = !!keyword.trim() && !!url.trim();
  const items = q.data ?? [];

  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle>내 추적 키워드</CardTitle>
          <CardDescription>
            매 2분 cron 이 24시간 이상 안 된 대상을 최대 3건씩 자동 갱신합니다
          </CardDescription>
        </div>
        <span className="text-xs text-gray-500">{items.length}개 등록</span>
      </div>

      <form
        className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (canAdd) add.mutate();
        }}
      >
        <Input
          placeholder="키워드"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Input
          placeholder="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Button type="submit" disabled={!canAdd || add.isPending}>
          {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
          추가
        </Button>
      </form>
      {add.isError && (
        <p className="mt-2 text-sm text-red-400">{(add.error as Error).message}</p>
      )}

      <div className="mt-6">
        {q.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}
        {q.data && items.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-6">
            아직 등록된 추적 키워드가 없습니다. 위에서 추가해 보세요.
          </p>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                  <th className="py-2 pr-2 font-medium">키워드</th>
                  <th className="py-2 pr-2 font-medium">URL</th>
                  <th className="py-2 pr-2 font-medium text-right">최근 순위</th>
                  <th className="py-2 pr-2 font-medium text-right">마지막 체크</th>
                  <th className="py-2 font-medium text-right">액션</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <TargetRow
                    key={t.id}
                    target={t}
                    isSelected={
                      !!selected &&
                      selected.keyword === t.keyword &&
                      selected.url === t.url
                    }
                    onShowChart={() => onSelect({ keyword: t.keyword, url: t.url })}
                    onDelete={() => del.mutate(t.id)}
                    isDeleting={del.isPending && del.variables === t.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

function TargetRow({
  target,
  isSelected,
  onShowChart,
  onDelete,
  isDeleting,
}: {
  target: RankTargetItem;
  isSelected: boolean;
  onShowChart: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const latest = target.latest;
  return (
    <tr
      className={cn(
        'border-b border-gray-900 last:border-b-0',
        isSelected ? 'bg-gold/5' : 'hover:bg-gray-950/60',
      )}
    >
      <td className="py-2 pr-2 font-medium text-white truncate max-w-[200px]" title={target.keyword}>
        {target.keyword}
      </td>
      <td className="py-2 pr-2 text-gray-300 truncate max-w-[280px]">
        <a
          href={target.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:text-gold"
          title={target.url}
        >
          <span className="truncate inline-block max-w-[260px]">{target.url}</span>
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      </td>
      <td className="py-2 pr-2 text-right">
        {latest == null ? (
          <span className="text-xs text-gray-500">대기 중</span>
        ) : latest.position == null ? (
          <span className="text-xs text-red-300">100위 밖</span>
        ) : (
          <span
            className={cn(
              'tabular font-bold',
              latest.position <= 10
                ? 'text-emerald-300'
                : latest.position <= 30
                ? 'text-gold'
                : 'text-gray-200',
            )}
          >
            {latest.position}위
          </span>
        )}
      </td>
      <td className="py-2 pr-2 text-right text-xs text-gray-500 tabular">
        {latest == null
          ? '-'
          : new Date(latest.checkedAt * 1000).toLocaleString('ko-KR', {
              month: 'numeric',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
      </td>
      <td className="py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={onShowChart}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
              isSelected
                ? 'bg-gold/20 text-gold'
                : 'text-gray-400 hover:text-gold hover:bg-gray-900',
            )}
            aria-label="30일 그래프 보기"
            title="30일 그래프 보기"
          >
            <LineChartIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50"
            aria-label="삭제"
            title="삭제"
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── 30일 이력 그래프 ──────────────────────────────────────

function HistoryCard({
  keyword,
  url,
  onClose,
}: {
  keyword: string;
  url: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['rank', 'history', keyword, url],
    queryFn: () => api.rank.history(keyword, url),
    staleTime: 30_000,
  });

  const chartData = useMemo(() => {
    const snaps = q.data?.snapshots ?? [];
    return snaps.map((s) => ({
      date: new Date(s.checkedAt * 1000).toLocaleDateString('ko-KR', {
        month: 'numeric',
        day: 'numeric',
      }),
      position: s.position ?? 101, // 100위 밖은 차트 상에 101위로 표기
      raw: s.position,
    }));
  }, [q.data]);

  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <CardTitle className="truncate">
            "{keyword}" — 30일 순위 변화
          </CardTitle>
          <CardDescription className="truncate">{url}</CardDescription>
        </div>
        <Button variant="secondary" size="sm" onClick={onClose}>
          닫기
        </Button>
      </div>

      <div className="mt-4">
        {q.isLoading && <Skeleton className="h-64 w-full" />}
        {q.data && chartData.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-800 py-12 text-center text-sm text-gray-500">
            아직 이력이 없습니다. 자동 갱신은 매 2분 cron 에서 1건씩 처리됩니다.
          </div>
        )}
        {chartData.length > 0 && (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} />
                <YAxis
                  reversed
                  domain={[1, 101]}
                  ticks={[1, 10, 30, 50, 100]}
                  stroke="#6b7280"
                  fontSize={11}
                  tickFormatter={(v) => (v === 101 ? '100+' : `${v}`)}
                  label={{
                    value: '순위',
                    angle: -90,
                    position: 'insideLeft',
                    fill: '#6b7280',
                    fontSize: 11,
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: '#d1d5db' }}
                  formatter={(_v, _n, p: { payload?: { raw?: number | null } }) => {
                    const raw = p?.payload?.raw;
                    return [raw == null ? '100위 밖' : `${raw}위`, '순위'];
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="position"
                  stroke="#FFD700"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#FFA500' }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}
